import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Checkbox,
  Flex,
  Text,
  Input,
  Select,
  HStack,
  VStack,
  useColorModeValue,
  Spinner,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Divider,
  InputGroup,
  InputLeftElement,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from '@chakra-ui/icons';
import { getToken } from '../../../../services/api';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_VERCEL_API_URL 
  : process.env.REACT_APP_LOCAL_API_URL;

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const PrecatoriosRS = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [selectedPrecatorios, setSelectedPrecatorios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEnte, setFilterEnte] = useState('');
  const [filterNatureza, setFilterNatureza] = useState('');
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  const [currentPriceRange, setCurrentPriceRange] = useState([0, 10000000]);
  const [savingSelection, setSavingSelection] = useState(false);
  
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const headingColor = useColorModeValue('gray.700', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('white', 'gray.800');
  const highlightColor = useColorModeValue('blue.50', 'blue.900');

  useEffect(() => {
    fetchPrecatorios();
  }, []);

  const fetchPrecatorios = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioRS`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar precatórios');
      }

      const data = await response.json();
      setPrecatorios(data.data || []);
      
      // Calculate min and max values
      const values = data.data.map(p => p.valor_atualizado || 0);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      setPriceRange([minValue, maxValue]);
      setCurrentPriceRange([minValue, maxValue]);
      
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao carregar precatórios',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    let sortableData = [...precatorios];
    if (sortConfig.key !== null) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [precatorios, sortConfig]);

  const filteredData = React.useMemo(() => {
    return sortedData.filter(item => {
      const matchesSearch = searchTerm === '' || 
        item.numero_precatorio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.natureza?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ente?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesEnte = filterEnte === '' || item.ente === filterEnte;
      const matchesNatureza = filterNatureza === '' || item.natureza === filterNatureza;
      const matchesPrice = item.valor_atualizado >= currentPriceRange[0] && 
                          item.valor_atualizado <= currentPriceRange[1];
      
      return matchesSearch && matchesEnte && matchesNatureza && matchesPrice;
    });
  }, [sortedData, searchTerm, filterEnte, filterNatureza, currentPriceRange]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentPageIds = paginatedData.map(item => item._id);
      setSelectedPrecatorios([...new Set([...selectedPrecatorios, ...currentPageIds])]);
    } else {
      const currentPageIds = paginatedData.map(item => item._id);
      setSelectedPrecatorios(selectedPrecatorios.filter(id => !currentPageIds.includes(id)));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedPrecatorios.includes(id)) {
      setSelectedPrecatorios(selectedPrecatorios.filter(item => item !== id));
    } else {
      setSelectedPrecatorios([...selectedPrecatorios, id]);
    }
  };

  const isAllSelected = paginatedData.length > 0 && 
    paginatedData.every(item => selectedPrecatorios.includes(item._id));

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePriceRangeChange = () => {
    setCurrentPriceRange(priceRange);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterEnte('');
    setFilterNatureza('');
    const values = precatorios.map(p => p.valor_atualizado || 0);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    setPriceRange([minValue, maxValue]);
    setCurrentPriceRange([minValue, maxValue]);
    setCurrentPage(1);
  };

  const handleSaveSelection = async () => {
    if (selectedPrecatorios.length === 0) {
      toast({
        title: 'Nenhum precatório selecionado',
        description: 'Por favor, selecione pelo menos um precatório',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSavingSelection(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioRS/selecionar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ selectedIds: selectedPrecatorios })
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar seleção');
      }

      const result = await response.json();
      
      toast({
        title: 'Seleção salva com sucesso',
        description: result.message,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Clear selection and refresh
      setSelectedPrecatorios([]);
      await fetchPrecatorios();
      
      // Navigate to selected page
      navigate('/admin/precatorios/rs/selecionadas');
      
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao salvar seleção',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingSelection(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const entes = [...new Set(precatorios.map(p => p.ente).filter(Boolean))].sort();
  const naturezas = [...new Set(precatorios.map(p => p.natureza).filter(Boolean))].sort();

  const totalValue = selectedPrecatorios.reduce((sum, id) => {
    const precatorio = precatorios.find(p => p._id === id);
    return sum + (precatorio?.valor_atualizado || 0);
  }, 0);

  const totalItems = filteredData.length;

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
            Precatórios Rio Grande do Sul
          </Text>
          <Text color={textColor} mt={1}>
            Total de {totalItems} precatórios encontrados
          </Text>
        </Box>
        <Button
          colorScheme="blue"
          onClick={onOpen}
          isDisabled={selectedPrecatorios.length === 0}
        >
          Ver Selecionados ({selectedPrecatorios.length})
        </Button>
      </Flex>

      {/* Filters */}
      <Box bg={bg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor} mb={6}>
        <VStack spacing={4} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 3, lg: 5 }} spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por número, natureza ou ente"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            
            <Select
              placeholder="Todos os entes"
              value={filterEnte}
              onChange={(e) => setFilterEnte(e.target.value)}
            >
              {entes.map(ente => (
                <option key={ente} value={ente}>{ente}</option>
              ))}
            </Select>
            
            <Select
              placeholder="Todas as naturezas"
              value={filterNatureza}
              onChange={(e) => setFilterNatureza(e.target.value)}
            >
              {naturezas.map(natureza => (
                <option key={natureza} value={natureza}>{natureza}</option>
              ))}
            </Select>
            
            <HStack>
              <Input
                type="number"
                placeholder="Valor mínimo"
                value={priceRange[0]}
                onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
              />
              <Text>-</Text>
              <Input
                type="number"
                placeholder="Valor máximo"
                value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
              />
            </HStack>
            
            <HStack>
              <Button size="sm" onClick={handlePriceRangeChange}>
                Aplicar Faixa
              </Button>
              <Button size="sm" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
            </HStack>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Table */}
      {loading ? (
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <>
          <TableContainer bg={bg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th width="50px">
                    <Checkbox
                      isChecked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort('ordem')}>
                    Ordem
                    {sortConfig.key === 'ordem' && (
                      sortConfig.direction === 'ascending' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />
                    )}
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort('numero_precatorio')}>
                    Número
                    {sortConfig.key === 'numero_precatorio' && (
                      sortConfig.direction === 'ascending' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />
                    )}
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort('natureza')}>
                    Natureza
                    {sortConfig.key === 'natureza' && (
                      sortConfig.direction === 'ascending' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />
                    )}
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort('orcamento')}>
                    Orçamento
                    {sortConfig.key === 'orcamento' && (
                      sortConfig.direction === 'ascending' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />
                    )}
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort('valor_atualizado')}>
                    Valor Atualizado
                    {sortConfig.key === 'valor_atualizado' && (
                      sortConfig.direction === 'ascending' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />
                    )}
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort('ente')}>
                    Ente
                    {sortConfig.key === 'ente' && (
                      sortConfig.direction === 'ascending' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />
                    )}
                  </Th>
                  <Th>Prioridade</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedData.map((item) => (
                  <Tr
                    key={item._id}
                    _hover={{ bg: hoverBg }}
                    bg={selectedPrecatorios.includes(item._id) ? highlightColor : 'transparent'}
                  >
                    <Td>
                      <Checkbox
                        isChecked={selectedPrecatorios.includes(item._id)}
                        onChange={() => handleSelectOne(item._id)}
                      />
                    </Td>
                    <Td>{item.ordem}</Td>
                    <Td>{item.numero_precatorio}</Td>
                    <Td>{item.natureza}</Td>
                    <Td>{item.orcamento}</Td>
                    <Td>{formatCurrency(item.valor_atualizado)}</Td>
                    <Td>{item.ente}</Td>
                    <Td>
                      {item.prioridade && (
                        <Badge colorScheme="orange">{item.prioridade}</Badge>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Flex justify="space-between" align="center" mt={4}>
            <HStack spacing={2}>
              <Text fontSize="sm" color={textColor}>
                Itens por página:
              </Text>
              <Select
                size="sm"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                width="80px"
              >
                {ITEMS_PER_PAGE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Select>
            </HStack>

            <HStack spacing={2}>
              <Button
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                Anterior
              </Button>
              <Text fontSize="sm" color={textColor}>
                Página {currentPage} de {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
              >
                Próxima
              </Button>
            </HStack>
          </Flex>
        </>
      )}

      {/* Selected Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Precatórios Selecionados</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={2} spacing={4}>
                <Stat>
                  <StatLabel>Total Selecionados</StatLabel>
                  <StatNumber>{selectedPrecatorios.length}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Valor Total</StatLabel>
                  <StatNumber fontSize="lg">{formatCurrency(totalValue)}</StatNumber>
                </Stat>
              </SimpleGrid>
              
              <Divider />
              
              <Box maxH="300px" overflowY="auto">
                <VStack spacing={2} align="stretch">
                  {selectedPrecatorios.map(id => {
                    const precatorio = precatorios.find(p => p._id === id);
                    return precatorio ? (
                      <Box key={id} p={2} borderWidth="1px" borderRadius="md">
                        <HStack justify="space-between">
                          <Text fontWeight="medium">{precatorio.numero_precatorio}</Text>
                          <Text>{formatCurrency(precatorio.valor_atualizado)}</Text>
                        </HStack>
                      </Box>
                    ) : null;
                  })}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Fechar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSaveSelection}
              isLoading={savingSelection}
              loadingText="Salvando..."
            >
              Salvar Seleção
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PrecatoriosRS;